import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyIcon from "@mui/icons-material/Key";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import {
  getRoles,
  getUserRole,
  getUserLocations,
  getUserFleets,
  getOrgLocationLevels,
  getOrgLocationMaster,
  updateUserRole,
  updateUserLocations,
  updateUserFleets,
} from "../../../Api/RbacAdminApi";
import { getFleets } from "../../../Api/FleetOverviewApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";
import CustomCheckbox from "../../Overview/components/Checkbox";
import { UNCATEGORIZED_SEGMENT } from "../../Overview/components/hierarchyFilterUtils";
import { useBaseEntityInfo, useOrgLabels } from "../../../Hooks/usePagesContent";

const PRIMARY_PURPLE = "#4D44D8";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

// ── Location tree helpers ────────────────────────────────────────────

// Build a hierarchical tree from flat org-location-master records.
// Each node carries a levelType (lowercased, e.g. "city") so the
// save payload can send { [levelType]: name } for each selected node.
// All node ids are strings "levelIdx:name" — uniqueness is per level+name,
// which mirrors the backend's location-object format.
// Returns { tree: [...], nodeMap: { id → node } }
const buildLocationTree = (records, sortedLevels) => {
  const levelKeys = sortedLevels.map((l) => l.level);
  const levelTypes = sortedLevels.map((l) => l.type.toLowerCase());
  const nodeMap = {};

  const buildGroup = (recs, levelIdx) => {
    if (levelIdx >= levelKeys.length) return [];
    const key = levelKeys[levelIdx];
    const levelType = levelTypes[levelIdx];
    const isDeepest = levelIdx === levelKeys.length - 1;

    const groups = {};
    recs.forEach((rec) => {
      const val = rec[key] || "Unknown";
      if (!groups[val]) groups[val] = [];
      groups[val].push(rec);
    });

    return Object.entries(groups).map(([name, groupRecs]) => {
      const id = `${levelIdx}:${name}`;
      const children = isDeepest ? [] : buildGroup(groupRecs, levelIdx + 1);
      const node = { id, name, levelType, children };
      nodeMap[id] = node;
      return node;
    });
  };

  const tree = buildGroup(records, 0);
  return { tree, nodeMap };
};

// All ids at every level in the subtree (not just leaves)
const getAllDescendantIds = (node) => {
  if (!node.children?.length) return [];
  return node.children.flatMap((child) => [child.id, ...getAllDescendantIds(child)]);
};

// Parse GET locations response → Set of node ids.
//
// Each assignment comes back as its full ancestor path, not just the granted
// node: a depot-level grant arrives as
//   { country: "India", zone: "West", state: "Gujarat",
//     city: "Gandhinagar", depot: "Gandhinagar Depot" }
// so only the DEEPEST populated level is the grant and everything above it is
// the route to it. Selecting every level that appears would render an ancestor
// as fully granted, and handleSave posts every selected node — so opening a
// user with a depot grant and saving would silently widen them to the whole
// country. Take the deepest level only and let the ancestors derive as
// indeterminate.
//
// A path whose deepest level is not in the tree (org-location-master no longer
// lists it) is skipped rather than falling back to its parent, since a parent
// would again claim more access than the assignment gives.
export const parseLocationResponse = (locations, sortedLevels, nodeMap) => {
  const ids = new Set();
  const list = Array.isArray(locations) ? locations.filter(Boolean) : [];

  // A level record with no usable type would make every node id below it
  // meaningless, and resolving a grant to a shallower level would claim more
  // access than it gives. Treat the whole config as unusable instead.
  const levelTypes = (sortedLevels || []).map((l) => (l?.type ? l.type.toLowerCase() : null));
  if (!levelTypes.length || levelTypes.some((type) => !type) || !nodeMap) {
    return { ids, unresolved: list.length };
  }

  let unresolved = 0;
  list.forEach((loc) => {
    for (let levelIdx = levelTypes.length - 1; levelIdx >= 0; levelIdx -= 1) {
      const name = loc[levelTypes[levelIdx]];
      if (!name) continue;
      const id = `${levelIdx}:${name}`;
      if (nodeMap[id]) ids.add(id);
      else unresolved += 1;
      return;
    }
    unresolved += 1;
  });

  return { ids, unresolved };
};

// ── Location tree node ──────────────────────────────────────────────
const LocationNode = ({ node, selectedIds, onToggle, depth, disabled }) => {
  const [open, setOpen] = useState(depth < 1);
  const isChecked = selectedIds.has(node.id);
  const descendantIds = getAllDescendantIds(node);
  const hasCheckedDescendant = descendantIds.some((id) => selectedIds.has(id));
  const isIndeterminate = !isChecked && hasCheckedDescendant;
  const hasChildren = node.children?.length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          pl: depth * 2.5,
          py: 0.4,
          borderRadius: 1,
          "&:hover": { backgroundColor: "#F5F5FF" },
        }}
      >
        <IconButton
          size="small"
          sx={{ p: 0.25, visibility: hasChildren ? "visible" : "hidden" }}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" sx={{ color: "#888" }} />
          ) : (
            <ChevronRightIcon fontSize="small" sx={{ color: "#888" }} />
          )}
        </IconButton>
        <CustomCheckbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          disabled={disabled}
          onChange={() => onToggle(node.id, descendantIds)}
        />
        <Typography sx={{ fontSize: 14, color: "#2E2E2E" }}>{node.name}</Typography>
      </Box>
      {open &&
        (node.children || []).map((child) => (
          <LocationNode
            key={child.id}
            node={child}
            selectedIds={selectedIds}
            onToggle={onToggle}
            depth={depth + 1}
            disabled={disabled}
          />
        ))}
    </Box>
  );
};

// ── Customer accounts, grouped by business segment ──────────────────
/**
 * Group the flat fleet list into [{ segment, fleets }] for the two-level picker.
 *
 * Segments appear in the order the API first mentions them and fleets keep their
 * order within a segment, matching how the global filter builds its segment list
 * so the two screens agree. A fleet with no category falls into the shared
 * "Uncategorized" bucket rather than vanishing.
 *
 * Grouping is presentation only. The selection state stays a flat Set of
 * fleetIDs and a segment is never itself selectable state - its checkbox is
 * derived from its children - so what the save sends is unchanged.
 *
 * A fleetID is placed once, under the first category that claims it. The fleets
 * API can list the same id under more than one category (buildCustomerNodes
 * dedupes for the same reason), and showing it twice would mean ticking it in
 * one group silently ticks it in the other, both counters counting it, and
 * clearing the second group revoking a grant just made in the first.
 */
export const groupFleetsBySegment = (fleets = []) => {
  const bySegment = new Map();
  const placed = new Set();
  (fleets || []).forEach((fleet) => {
    if (placed.has(fleet?.fleetID)) return;
    placed.add(fleet?.fleetID);
    const segment = fleet?.category || UNCATEGORIZED_SEGMENT;
    if (!bySegment.has(segment)) bySegment.set(segment, []);
    bySegment.get(segment).push(fleet);
  });
  return [...bySegment.entries()].map(([segment, segmentFleets]) => ({
    segment,
    fleets: segmentFleets,
  }));
};

// The entity and location words are org-configurable, so they are passed in
// rather than hardcoded - this helper is module level and cannot call a hook.
const fleetSubtitle = (fleet, entityPluralLabel, locationPluralLabel) => {
  const parts = [
    fleet.vehicleCount != null && `${fleet.vehicleCount} ${entityPluralLabel.toLowerCase()}`,
    fleet.depotCount != null && `${fleet.depotCount} ${locationPluralLabel.toLowerCase()}`,
  ].filter(Boolean);
  return parts.length ? parts.join(" \u00b7 ") : null;
};

/**
 * Toggle a whole segment inside the flat selection Set.
 *
 * Exported and pure because it is where the user's hard constraint lives: only
 * values drawn from fleetIds ever enter the Set, so a segment name cannot reach
 * the save payload. `allSelected` is recomputed from `prevSelected` rather than
 * taken from the render that produced the click, so two clicks landing in one
 * React batch cannot both act on the same stale answer.
 */
export const applySegmentToggle = (prevSelected, fleetIds = []) => {
  const next = new Set(prevSelected);
  const allSelected = fleetIds.length > 0 && fleetIds.every((id) => next.has(id));
  fleetIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
  return next;
};

/**
 * One business segment and the customer accounts under it.
 *
 * The segment checkbox is derived, never stored: checked when every account
 * below it is selected, indeterminate when only some are. Clicking it selects
 * or clears the whole segment in one go, which is the point of the grouping -
 * granting "all Tarmac accounts" was previously a dozen individual clicks.
 */
const FleetSegmentGroup = ({
  segment,
  fleets,
  selectedFleetIds,
  onToggleFleet,
  onToggleSegment,
  entityPluralLabel,
  locationPluralLabel,
  disabled,
}) => {
  const [open, setOpen] = useState(true);
  const fleetIds = fleets.map((fleet) => fleet.fleetID);
  const allSelected =
    fleetIds.length > 0 && fleetIds.every((id) => selectedFleetIds.has(id));
  const isChecked = disabled || allSelected;
  const isIndeterminate = !isChecked && fleetIds.some((id) => selectedFleetIds.has(id));
  // Grant-all ticks every box below, so a count of the individual selections
  // would read "(0/5)" beside five ticked checkboxes and contradict them.
  const selectedCount = disabled
    ? fleetIds.length
    : fleetIds.filter((id) => selectedFleetIds.has(id)).length;

  return (
    <Box>
      <Box
        onClick={() => !disabled && onToggleSegment(fleetIds)}
        data-testid={`fleet-segment-${segment}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          py: 0.4,
          borderRadius: 1,
          cursor: disabled ? "default" : "pointer",
          "&:hover": { backgroundColor: disabled ? undefined : "#F5F5FF" },
        }}
      >
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          aria-label={open ? `Collapse ${segment}` : `Expand ${segment}`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        >
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" sx={{ color: "#888" }} />
          ) : (
            <ChevronRightIcon fontSize="small" sx={{ color: "#888" }} />
          )}
        </IconButton>
        <CustomCheckbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          disabled={disabled}
          onChange={() => onToggleSegment(fleetIds)}
          onClick={(e) => e.stopPropagation()}
        />
        <Typography sx={{ fontSize: 14, color: "#2E2E2E" }}>{segment}</Typography>
        <Typography sx={{ fontSize: 12, color: "#A2A2A2", ml: 0.75 }}>
          {selectedCount}/{fleetIds.length}
        </Typography>
      </Box>

      {open &&
        fleets.map((fleet) => {
          const id = fleet.fleetID;
          const subtitle = fleetSubtitle(fleet, entityPluralLabel, locationPluralLabel);
          return (
            <Box
              key={id}
              onClick={() => !disabled && onToggleFleet(id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                // One level in, the way a depot sits under its city in the
                // location tree.
                pl: 2.5,
                py: 0.4,
                borderRadius: 1,
                cursor: disabled ? "default" : "pointer",
                "&:hover": { backgroundColor: disabled ? undefined : "#F5F5FF" },
              }}
            >
              {/* A leaf has nothing to expand but still occupies the gutter, so
                  its checkbox lines up under its parent's. Hidden rather than
                  sized by hand, exactly as LocationNode does it, so the two
                  trees cannot drift apart if the icon size changes. */}
              <IconButton size="small" sx={{ p: 0.25, visibility: "hidden" }} tabIndex={-1}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              <CustomCheckbox
                checked={disabled || selectedFleetIds.has(id)}
                disabled={disabled}
                onChange={() => onToggleFleet(id)}
                onClick={(e) => e.stopPropagation()}
              />
              <Box>
                <Typography sx={{ fontSize: 14, color: "#2E2E2E" }}>
                  {fleet.fleetName}
                </Typography>
                {subtitle && (
                  <Typography sx={{ fontSize: 12, color: "#888" }}>{subtitle}</Typography>
                )}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────
const UserEditDialog = ({ user, onBack, onSuccess }) => {
  const { openNotification } = useToast();
  // Every customer-account / business-segment string on this screen comes from
  // orgMeta globalConfig.labels via useOrgLabels, the same source the global
  // filter reads, so an org that calls them something else is renamed in one
  // place rather than in each screen's copy.
  const {
    fleetLabel,
    fleetPluralLabel,
    fleetCategoryLabel,
    locationLabel,
    locationPluralLabel,
  } = useOrgLabels();
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [availableRoles, setAvailableRoles] = useState([]);
  const [locationTree, setLocationTree] = useState([]);
  const [nodeMap, setNodeMap] = useState({});
  const [availableFleets, setAvailableFleets] = useState([]);

  const [selectedRoleId, setSelectedRoleId] = useState("");
  // Both "grant all" switches default to off. They are what a save posts, so a
  // read that never lands must not leave the dialog offering unlimited access.
  const [allLocationsAccess, setAllLocationsAccess] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
  const [allFleetsAccess, setAllFleetsAccess] = useState(false);
  const [selectedFleetIds, setSelectedFleetIds] = useState(new Set());

  const [initial, setInitial] = useState(null);
  // A read that did not land leaves both panels showing default-deny, which is
  // indistinguishable from a user who genuinely has no access. Saving from that
  // state would replace their real assignments with what is on screen, so Save
  // stays disabled until the dialog has been loaded from a complete read.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (user) fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [
        rolesRes,
        userRoleRes,
        userLocRes,
        userFleetRes,
        levelsRes,
        masterRes,
        fleetsRes,
      ] = await Promise.all([
        getRoles(),
        getUserRole(user.userId),
        getUserLocations(user.userId),
        getUserFleets(user.userId),
        getOrgLocationLevels(),
        getOrgLocationMaster(),
        getFleets(),
      ]);

      // ── Roles ──
      const roles =
        rolesRes?.responseStatus?.code === CODE.SUCCESS
          ? rolesRes.response?.data || []
          : [];
      setAvailableRoles(roles);

      let initRoleId = "";
      if (userRoleRes?.responseStatus?.code === CODE.SUCCESS) {
        const rd = userRoleRes.response?.data || {};
        if (rd.roleId) {
          initRoleId = rd.roleId;
        } else if (rd.roleCode) {
          initRoleId = roles.find((r) => r.roleCode === rd.roleCode)?.id || "";
        }
        setSelectedRoleId(initRoleId);
      }

      // ── Location tree ──
      let builtTree = [];
      let builtNodeMap = {};
      let builtLevels = [];
      if (
        levelsRes?.responseStatus?.code === CODE.SUCCESS &&
        masterRes?.responseStatus?.code === CODE.SUCCESS
      ) {
        const rawLevels = levelsRes.response?.data || levelsRes.data || [];
        const rawRecords = masterRes.response?.data || masterRes.data || [];

        const levelNumber = (level) => {
          const parsed = parseInt(String(level?.level || "").replace("level", ""), 10);
          return Number.isNaN(parsed) ? 0 : parsed;
        };
        const sortedLevels = [...rawLevels].sort((a, b) => levelNumber(a) - levelNumber(b));

        builtLevels = sortedLevels;

        const result = buildLocationTree(rawRecords, sortedLevels);
        builtTree = result.tree;
        builtNodeMap = result.nodeMap;
        setLocationTree(builtTree);
        setNodeMap(builtNodeMap);
      }

      // ── User location access ──
      let initAllLoc = false;
      let initNodeIds = new Set();
      let unresolvedLocations = 0;
      if (userLocRes?.responseStatus?.code === CODE.SUCCESS) {
        const d = userLocRes.response?.data || {};
        initAllLoc = d.hasAllAccess ?? d.allAccess ?? false;
        // GET returns each assignment as its full ancestor path; only the
        // deepest level of each is the grant.
        const parsed = parseLocationResponse(d.locations || [], builtLevels, builtNodeMap);
        initNodeIds = parsed.ids;
        unresolvedLocations = parsed.unresolved;
        setAllLocationsAccess(initAllLoc);
        setSelectedNodeIds(initNodeIds);
      }

      // ── Fleets ──
      const fleets =
        fleetsRes?.responseStatus?.code === CODE.SUCCESS
          ? fleetsRes.response?.data || []
          : [];
      setAvailableFleets(fleets);

      let initAllFleet = false;
      let initFleetIds = new Set();
      if (userFleetRes?.responseStatus?.code === CODE.SUCCESS) {
        const d = userFleetRes.response?.data || {};
        initAllFleet = d.hasAllAccess ?? d.allAccess ?? false;
        const returnedNames = d.fleetNames || d.fleets || d.fleetIds || [];
        initFleetIds = new Set(
          returnedNames
            .map(
              (name) =>
                fleets.find((f) => f.fleetName === name || f.fleetID === name)
                  ?.fleetID
            )
            .filter(Boolean)
        );
        setAllFleetsAccess(initAllFleet);
        setSelectedFleetIds(initFleetIds);
      }

      setInitial({
        roleId: initRoleId,
        allLocationsAccess: initAllLoc,
        locationNodeIds: initNodeIds,
        allFleetsAccess: initAllFleet,
        fleetIds: initFleetIds,
      });

      // Default-deny is only safe if it is visible: a read that failed leaves the
      // panels looking like "no access", which is indistinguishable from a user
      // who genuinely has none. Disable Save and say so, rather than let an admin
      // replace their real assignments with what is on screen.
      const readFailed = [
        levelsRes,
        masterRes,
        userLocRes,
        userFleetRes,
        fleetsRes,
      ].some((res) => res?.responseStatus?.code !== CODE.SUCCESS);
      setLoadFailed(readFailed);
      if (readFailed) {
        openNotification({
          text: "Could not load this user's access - reload before saving",
          icon: "toast-error",
        });
      } else if (unresolvedLocations > 0) {
        // The grant exists but its location is gone from the org hierarchy, so no
        // checkbox can represent it. A save replaces the whole list, which would
        // drop it - the admin has to know before editing anything.
        openNotification({
          text: `${unresolvedLocations} location grant(s) no longer exist - saving drops them`,
          icon: "toast-error",
        });
      }
    } catch (error) {
      // No panel can be trusted after a throw, and the same reasoning applies:
      // show default-deny, keep Save disabled, and tell the admin.
      setLoadFailed(true);
      openNotification({
        text: "Could not load this user's access - reload before saving",
        icon: "toast-error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle a location node: check/uncheck it and remove redundant child selections
  const handleLocationToggle = (nodeId, descendantIds) => {
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        // Uncheck this node (and any descendants that might be selected)
        next.delete(nodeId);
        descendantIds.forEach((id) => next.delete(id));
      } else {
        // Check this node; remove child selections (parent now covers them)
        next.add(nodeId);
        descendantIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const handleFleetToggle = (fleetId) => {
    setSelectedFleetIds((prev) => {
      const next = new Set(prev);
      next.has(fleetId) ? next.delete(fleetId) : next.add(fleetId);
      return next;
    });
  };

  // A segment click is a bulk edit of its own accounts and nothing else: the
  // segment name never enters the set, so the payload stays a flat list of
  // fleetIDs and neither the save call nor the API has to know about grouping.
  const fleetSegments = useMemo(
    () => groupFleetsBySegment(availableFleets),
    [availableFleets]
  );

  const handleSegmentToggle = (fleetIds) => {
    setSelectedFleetIds((prev) => applySegmentToggle(prev, fleetIds));
  };

  const setsEqual = (a, b) =>
    a.size === b.size && [...a].every((id) => b.has(id));

  const handleSave = async () => {
    if (!initial) return;
    setSaving(true);
    try {
      const selectedRole = availableRoles.find((r) => r.id === selectedRoleId);
      const roleChanged = selectedRoleId !== initial.roleId;
      const locChanged =
        allLocationsAccess !== initial.allLocationsAccess ||
        !setsEqual(selectedNodeIds, initial.locationNodeIds);
      const fleetChanged =
        allFleetsAccess !== initial.allFleetsAccess ||
        !setsEqual(selectedFleetIds, initial.fleetIds);

      if (!roleChanged && !locChanged && !fleetChanged) {
        openNotification({ text: "No changes to save", icon: "toast-success" });
        return;
      }

      const calls = [];
      if (roleChanged && selectedRole) {
        calls.push(updateUserRole(user.userId, { roleCode: selectedRole.roleCode }));
      }
      if (locChanged) {
        // Build payload: [{ [levelType]: name }, ...]
        const locationObjects = allLocationsAccess
          ? []
          : [...selectedNodeIds]
              .map((id) => {
                const node = nodeMap[id];
                return node ? { [node.levelType]: node.name } : null;
              })
              .filter(Boolean);
        calls.push(
          updateUserLocations(user.userId, {
            hasAllAccess: allLocationsAccess,
            locations: locationObjects,
          })
        );
      }
      if (fleetChanged) {
        const fleetNames = allFleetsAccess
          ? []
          : [...selectedFleetIds]
              .map((id) => availableFleets.find((f) => f.fleetID === id)?.fleetName)
              .filter(Boolean);
        calls.push(
          updateUserFleets(user.userId, {
            ...(allFleetsAccess && { hasAllAccess: true }),
            fleetNames,
          })
        );
      }

      const results = await Promise.all(calls);
      const allSucceeded = results.every(
        (r) => r?.responseStatus?.code === CODE.SUCCESS
      );
      if (!allSucceeded) {
        const failed = results.find((r) => r?.responseStatus?.code !== CODE.SUCCESS);
        openNotification({
          text: failed?.responseStatus?.msg || "Failed to save changes",
          icon: "toast-error",
        });
      } else {
        openNotification({ text: "User assignments saved", icon: "toast-success" });
        onSuccess();
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = availableRoles.find((r) => r.id === selectedRoleId);

  return (
    <Box sx={{ backgroundColor: "#F5F5F8", minHeight: "100%" }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 2,
          backgroundColor: "#fff",
          borderBottom: "1px solid #EBEBEB",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            onClick={onBack}
            startIcon={<ChevronLeftIcon />}
            sx={{
              color: "#555",
              fontWeight: 600,
              fontSize: 15,
              textTransform: "none",
              px: 1,
              border: "1px solid #E0E0E0",
              borderRadius: 1.5,
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            Back
          </Button>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              fontSize: 18,
              fontWeight: 700,
              background: "linear-gradient(135deg, #6B63E8 0%, #4D44D8 100%)",
            }}
          >
            {getInitials(user.name)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#2E2E2E" }}>
              {user.name}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#888" }}>{user.email}</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || loadFailed}
          sx={{ fontSize: 15, px: 3, py: 1.25 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save changes"}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 3, p: 4, alignItems: "flex-start" }}>
          {/* ── Role Assignment card ── */}
          <Paper variant="outlined" sx={{ flex: 1, p: 3, borderRadius: 2, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <KeyIcon sx={{ color: PRIMARY_PURPLE, fontSize: 20 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#2E2E2E" }}>
                Role Assignment
              </Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: 15 }}>Role</InputLabel>
              <Select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                label="Role"
                sx={{ fontSize: 15 }}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id} sx={{ fontSize: 15 }}>
                    {role.roleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRole?.roleDescription && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: "#F0F4FF",
                  borderRadius: 1.5,
                  border: "1px solid #D6E0FF",
                }}
              >
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: PRIMARY_PURPLE, mb: 0.5 }}
                >
                  Role Info
                </Typography>
                <Typography sx={{ fontSize: 13, color: PRIMARY_PURPLE }}>
                  {selectedRole.roleDescription}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* ── Location Access card ── */}
          <Paper variant="outlined" sx={{ flex: 1.3, p: 3, borderRadius: 2, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <LocationOnIcon sx={{ color: "#22C55E", fontSize: 20 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#2E2E2E" }}>
                {`${locationLabel} Access`}
              </Typography>
            </Box>

            <Box
              onClick={() => setAllLocationsAccess((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                cursor: "pointer",
                backgroundColor: allLocationsAccess ? "#F0FFF4" : "#FAFAFA",
                border: `1px solid ${allLocationsAccess ? "#BBF7D0" : "#E8E8E8"}`,
              }}
            >
              <CustomCheckbox
                checked={allLocationsAccess}
                onChange={() => setAllLocationsAccess((v) => !v)}
                onClick={(e) => e.stopPropagation()}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: allLocationsAccess ? "#166534" : "#555",
                }}
              >
                {`Grant access to all ${locationPluralLabel.toLowerCase()}`}
              </Typography>
            </Box>

            <Box
              sx={{
                border: "1px solid #EBEBEB",
                borderRadius: 1.5,
                p: 1.5,
                maxHeight: 380,
                overflowY: "auto",
              }}
            >
              {locationTree.length === 0 ? (
                <Typography
                  sx={{ fontSize: 13, color: "#888", textAlign: "center", py: 2 }}
                >
                  {`No ${locationPluralLabel.toLowerCase()} available`}
                </Typography>
              ) : (
                locationTree.map((node) => (
                  <LocationNode
                    key={node.id}
                    node={node}
                    selectedIds={selectedNodeIds}
                    onToggle={handleLocationToggle}
                    depth={0}
                    disabled={allLocationsAccess}
                  />
                ))
              )}
            </Box>
          </Paper>

          {/* ── Customer accounts card, grouped by business segment ── */}
          <Paper variant="outlined" sx={{ flex: 1, p: 3, borderRadius: 2, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <DirectionsBusIcon sx={{ color: PRIMARY_PURPLE, fontSize: 20 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#2E2E2E" }}>
                {`${fleetLabel} Access`}
              </Typography>
            </Box>

            <Box
              onClick={() => setAllFleetsAccess((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                cursor: "pointer",
                backgroundColor: allFleetsAccess ? "#F0F4FF" : "#FAFAFA",
                border: `1px solid ${allFleetsAccess ? "#C7D2FE" : "#E8E8E8"}`,
              }}
            >
              <CustomCheckbox
                checked={allFleetsAccess}
                onChange={() => setAllFleetsAccess((v) => !v)}
                onClick={(e) => e.stopPropagation()}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: allFleetsAccess ? PRIMARY_PURPLE : "#555",
                }}
              >
                {`Grant access to all ${fleetPluralLabel.toLowerCase()}`}
              </Typography>
            </Box>

            {/* One bordered, scrolling container holding the whole tree, matching
                the Operating Location card beside it. The two pickers do the same
                job and now read as the same control; only the cascade differs -
                a segment grants every account under it, whereas a location grant
                is stored at the level it was made. */}
            <Box
              sx={{
                border: "1px solid #EBEBEB",
                borderRadius: 1.5,
                p: 1.5,
                maxHeight: 380,
                overflowY: "auto",
              }}
            >
              {fleetSegments.length === 0 ? (
                <Typography
                  sx={{ fontSize: 13, color: "#888", textAlign: "center", py: 2 }}
                >
                  {`No ${fleetPluralLabel.toLowerCase()} available`}
                </Typography>
              ) : (
                fleetSegments.map(({ segment, fleets }) => (
                  <FleetSegmentGroup
                    key={segment}
                    segment={segment}
                    fleets={fleets}
                    selectedFleetIds={selectedFleetIds}
                    onToggleFleet={handleFleetToggle}
                    onToggleSegment={handleSegmentToggle}
                    entityPluralLabel={baseEntityTypePluralLable}
                    locationPluralLabel={locationPluralLabel}
                    disabled={allFleetsAccess}
                  />
                ))
              )}
            </Box>
            <Typography sx={{ fontSize: 12, color: "#888", mt: 1.5 }}>
              {`Select a ${fleetCategoryLabel.toLowerCase()} to grant every ${fleetLabel.toLowerCase()} under it.`}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default UserEditDialog;
